import { Plus, Trash2, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useActionsRapports, useAgents, useListesDiffusion } from '@/api/hooks';
import type { ListeDiffusion } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateHeureFr } from '@/lib/utils';
import { OngletsAdministration } from './Onglets';

const EXTERNE = '__externe__';

/** Formulaire d'ajout : un agent du référentiel (adresse reprise de sa fiche) ou une adresse externe. */
function AjoutDestinataire({ liste }: { liste: ListeDiffusion }) {
  const agents = useAgents();
  const actions = useActionsRapports();
  const [agentId, setAgentId] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const dejaPresents = new Set(liste.destinataires.map((d) => d.email.toLowerCase()));
  const candidats = (agents.data ?? []).filter((a) => a.email && !dejaPresents.has(a.email.toLowerCase())).sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
  const externe = agentId === EXTERNE;
  const valide = externe ? nom.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) : agentId.length > 0;
  const ajouter = async () => {
    try {
      await actions.ajouterDestinataire.mutateAsync(externe ? { perimetre: liste.perimetre, nom: nom.trim(), email: email.trim() } : { perimetre: liste.perimetre, agentId });
      toast.success(`Destinataire ajouté à la liste ${liste.perimetre}.`);
      setAgentId('');
      setNom('');
      setEmail('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ajout impossible.');
    }
  };
  return (
    <div className="grid gap-3 rounded-md border bg-muted/30 p-3 sm:grid-cols-[1fr_auto] sm:items-end">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5 sm:col-span-3">
          <Label htmlFor={`agent-${liste.perimetre}`}>Agent du référentiel ou adresse externe</Label>
          <SelectNatif id={`agent-${liste.perimetre}`} value={agentId} onChange={(e) => setAgentId(e.target.value)}>
            <option value="">Choisir…</option>
            <option value={EXTERNE}>Adresse externe (hors référentiel)</option>
            {candidats.map((a) => (
              <option key={a.id} value={a.id}>
                {a.prenom} {a.nom} · {a.fonction}{a.structureCode ? ` (${a.structureCode})` : ''}
              </option>
            ))}
          </SelectNatif>
        </div>
        {externe && (
          <>
            <div className="space-y-1.5 sm:col-span-1">
              <Label htmlFor={`nom-${liste.perimetre}`}>Nom</Label>
              <Input id={`nom-${liste.perimetre}`} value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Cabinet du Ministre" maxLength={120} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor={`email-${liste.perimetre}`}>Adresse de courriel</Label>
              <Input id={`email-${liste.perimetre}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cabinet@ministere.gouv.cd" maxLength={160} />
            </div>
          </>
        )}
      </div>
      <Button onClick={ajouter} disabled={!valide || actions.ajouterDestinataire.isPending}>
        <Plus className="size-4" /> Ajouter
      </Button>
    </div>
  );
}

function Liste({ liste, modifiable }: { liste: ListeDiffusion; modifiable: boolean }) {
  const actions = useActionsRapports();
  const retirer = async (id: string, nom: string) => {
    try {
      await actions.retirerDestinataire.mutateAsync(id);
      toast.success(`${nom} retiré de la liste ${liste.perimetre}.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retrait impossible.');
    }
  };
  const reprendre = async (agentId: string, nom: string) => {
    try {
      await actions.ajouterDestinataire.mutateAsync({ perimetre: liste.perimetre, agentId });
      toast.success(`${nom} ajouté à la liste ${liste.perimetre}.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ajout impossible.');
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {liste.libelle} <Badge variant="outline">{liste.perimetre}</Badge>
          {liste.destinataires.length ? <Badge variant="secondary">liste administrée · {liste.destinataires.length}</Badge> : <Badge variant="outline">destinataires par défaut</Badge>}
        </CardTitle>
        <CardDescription>
          {liste.destinataires.length
            ? 'Les rapports de ce périmètre sont envoyés à cette liste (les destinataires par défaut ne sont plus utilisés).'
            : liste.perimetre === 'FONDS'
              ? 'Sans liste, le rapport du Fonds est envoyé à la Direction Générale, au Directeur financier et au contrôle de gestion.'
              : 'Sans liste, le rapport de la direction est envoyé à son ou ses directeurs habilités.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {liste.destinataires.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Adresse</TableHead>
                <TableHead className="hidden md:table-cell">Ajouté</TableHead>
                {modifiable && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {liste.destinataires.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>
                    {d.nom} {!d.agentId && <Badge variant="outline">externe</Badge>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{d.email}</TableCell>
                  <TableCell className="hidden text-xs text-muted-foreground md:table-cell">
                    {d.creeParNom} · {dateHeureFr(d.createdAt)}
                  </TableCell>
                  {modifiable && (
                    <TableCell>
                      <Button size="icon" variant="ghost" onClick={() => retirer(d.id, d.nom)} title="Retirer" aria-label={`Retirer ${d.nom}`} disabled={actions.retirerDestinataire.isPending}>
                        <Trash2 className="size-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {liste.destinataires.length === 0 && (
          <div>
            <div className="mb-1 text-sm font-medium">Destinataires par défaut</div>
            {liste.parDefaut.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucun agent habilité : les rapports de ce périmètre ne seront pas envoyés tant qu'une liste n'est pas définie.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {liste.parDefaut.map((d) => (
                  <li key={d.agentId} className="flex flex-wrap items-center gap-2">
                    <span>{d.nom}</span>
                    <span className="text-muted-foreground">{d.email}</span>
                    <Badge variant="outline">{d.motif}</Badge>
                    {modifiable && (
                      <Button size="sm" variant="ghost" onClick={() => reprendre(d.agentId, d.nom)} title="Reprendre dans la liste administrée" disabled={actions.ajouterDestinataire.isPending}>
                        <UserPlus className="size-4" /> Reprendre
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {modifiable && <AjoutDestinataire liste={liste} />}
      </CardContent>
    </Card>
  );
}

/** Listes de destinataires des rapports d'exécution par périmètre (cahier des charges du tableau de bord, § 11.3), gérées dans Paramètres. */
export function DestinatairesRapports() {
  const session = useSession();
  const profils = session?.utilisateur.profils ?? [];
  const modifiable = profils.some((p) => ['ADMINISTRATEUR', 'DIRECTEUR_GENERAL'].includes(p));
  const { data, isLoading, error } = useListesDiffusion();
  const [perimetre, setPerimetre] = useState('FONDS');
  const liste = data?.find((l) => l.perimetre === perimetre) ?? data?.[0];
  return (
    <div>
      <PageHeader
        titre="Administration"
        sousTitre="Destinataires des rapports d'exécution : chaque rapport mensuel part en PDF joint, depuis l'adresse institutionnelle, aux destinataires du périmètre ; chaque envoi est tracé dans la bibliothèque."
        actions={
          data && (
            <SelectNatif value={liste?.perimetre ?? 'FONDS'} onChange={(e) => setPerimetre(e.target.value)} aria-label="Périmètre">
              {data.map((l) => (
                <option key={l.perimetre} value={l.perimetre}>
                  {l.perimetre} · {l.libelle}
                  {l.destinataires.length ? ` (${l.destinataires.length})` : ''}
                </option>
              ))}
            </SelectNatif>
          )
        }
      />
      <OngletsAdministration />
      {isLoading ? <Chargement /> : error || !data ? <ErreurBloc erreur={error} /> : !liste ? <Vide message="Aucun périmètre." /> : <Liste key={liste.perimetre} liste={liste} modifiable={modifiable} />}
    </div>
  );
}
